import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Download, Database, Calendar } from "lucide-react";

export default function Backup() {
  const { user } = useAuth();
  const allowed = ["partner", "admin"].includes(user?.role);
  const [busy, setBusy] = useState(false);

  if (!allowed) {
    return <div className="text-sm text-slate-500" data-testid="backup-forbidden">Not authorised.</div>;
  }

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "_");
      a.href = url; a.download = `vcaj_backup_${ts}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="backup-page">
      <div>
        <div className="eyebrow">Backup</div>
        <h1 className="font-sans text-4xl mt-2">Data backup & export</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="rounded-sm bg-slate-100 p-3"><Database className="h-6 w-6 text-slate-700"/></div>
          <div className="flex-1">
            <h2 className="font-sans text-2xl">On-demand backup</h2>
            <p className="text-sm text-slate-600 mt-1">Download a full ZIP containing every collection as CSV — clients, projects, timesheets, payroll, invoices, expenses, leaves, and more. Password hashes are excluded.</p>
            <Button onClick={download} disabled={busy} className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="backup-download-btn">
              <Download className="h-4 w-4 mr-2"/>{busy ? "Generating..." : "Download Backup (ZIP)"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-sm bg-slate-100 p-3"><Calendar className="h-6 w-6 text-slate-700"/></div>
          <div className="flex-1">
            <h2 className="font-sans text-2xl">Daily auto-backup</h2>
            <p className="text-sm text-slate-600 mt-1">A snapshot ZIP is generated automatically every day at midnight IST and retained for 30 days. (Coming soon — currently rely on on-demand download below.)</p>
            <p className="text-xs text-amber-700 mt-2">Note: Automated scheduling requires deploying with a cron worker; today the on-demand button above provides equivalent functionality.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
